<script setup>
import Layout from "../../../layouts/main.vue";
import { onMounted, reactive, ref } from "vue";
import { useRouter, useRoute } from "vue-router";
import MethodService from "../../../service/MethodService";
import DataForm from "./dataFormSearchFilter";
import mushroom from "cem-primary-api";
import Swal from "sweetalert2";
import toastr from "toastr";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
let loading = ref(false);
const router = useRouter();
const route = useRoute();
const tableRules = reactive(MethodService.copyObject(DataForm.tableRules));
const dataTables = reactive({ value: [] });
const pageSizeDefault = ref(20);
const strategyOptions = ref([]);

// lấy danh sách hướng tìm kiếm cho filter
const getStrategyOptions = async () => {
  try {
    const response = await mushroom.search_strategy.getAllAsync({
      fields: "id,name",
    });
    if (response.result) {
      strategyOptions.value = response.result.map((item) => ({
        value: item.id,
        label: item.name?.vi || item.name?.en || item.id,
      }));
    }
  } catch (e) {
    console.error("Có lỗi: %o", e);
  }
};

const getStrategyName = (strategyId) => {
  const found = strategyOptions.value.find((s) => s.value === strategyId);
  return found ? found.label : "";
};

const fn_handle = async (type, scope, row) => {
  if (type === "update") {
    router.push({
      name: "CapNhatSearchFilter",
      params: { id: row.id },
    });
  }
  if (type === "delete") {
    Swal.fire({
      title: t("t_warning"),
      text: t("t_record_delete"),
      icon: "warning",
      confirmButtonColor: "#f46a6a",
      confirmButtonText: t("swal_accept"),
      cancelButtonText: t("swal_cancel"),
      showCancelButton: true,
      allowOutsideClick: false,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await mushroom.search_filter.deleteAsync({
            id: row.id,
          });
          if (response.result) {
            toastr.success(t("toastr_delete_success"));
            getListSearchFilter();
          }
        } catch (e) {
          console.error("Có lỗi: %o", e);
          MethodService.showError(e.code);
        }
      }
    });
  }
};

// hiển thị form tìm kiếm
const fn_showFormTimKiem = () => {
  tableRules.showFormSearch = !tableRules.showFormSearch;
};

// tìm kiếm
const fn_TimKiem = () => {
  tableRules.dataSearch.value["code"] = tableRules.dataSearch.value["code"]
    ? tableRules.dataSearch.value["code"].trim()
    : "";
  tableRules.dataSearch.value["name.vi"] = tableRules.dataSearch.value[
    "name.vi"
  ]
    ? tableRules.dataSearch.value["name.vi"].trim()
    : "";
  tableRules.dataSearch.value["name.en"] = tableRules.dataSearch.value[
    "name.en"
  ]
    ? tableRules.dataSearch.value["name.en"].trim()
    : "";

  tableRules.filters = MethodService.filterTable(
    JSON.stringify(tableRules.dataSearch),
  );
  tableRules.offset = 0;
  tableRules.sort = "";
  getListSearchFilter();
};

// phân trang
const fn_tableSizeChange = (limit) => {
  tableRules.limit = limit;
  pageSizeDefault.value = limit;
  fn_tableChangeOffset(tableRules.page);
};
const fn_tableCurentChange = (page) => {
  fn_tableChangeOffset(page);
};
const fn_tablePrevClick = (page) => {
  fn_tableChangeOffset(page - 1);
};
const fn_tableNextClick = (page) => {
  fn_tableChangeOffset(page + 1);
};
const fn_tableChangeOffset = (page) => {
  tableRules.page = page;
  tableRules.offset = (tableRules.page - 1) * pageSizeDefault.value;
  getListSearchFilter();
};
const fn_tableSortChange = (column) => {
  MethodService.tableSortChange(column, tableRules);
  loading.value = true;
  getListSearchFilter();
};

const fn_hashUrl = () => {
  MethodService.hashUrl(route.query, tableRules);
  pageSizeDefault.value = tableRules.limit;
};

// chuyển sang màn thêm mới
const fn_ThemMoi = () => {
  const query = {};
  const queryStrategyId =
    route.query?.search_strategy_id ||
    tableRules.dataSearch.value["search_strategy_id"];
  if (queryStrategyId) {
    query.search_strategy_id = queryStrategyId;
  }
  router.push({ name: "ThemMoiSearchFilter", query });
};

// lấy danh sách
const getListSearchFilter = async () => {
  loading.value = true;
  let dataFilter = {
    filters: tableRules.filters,
    fields: tableRules.fields,
    limit: tableRules.limit,
    offset: tableRules.offset,
    sort: tableRules.sort,
  };
  const replaceQuery = {
    limit: tableRules.limit,
    page: tableRules.page,
    sort: tableRules.sort,
    filters: tableRules.filters,
    showFormSearch: tableRules.showFormSearch,
  };
  // Giữ search_strategy_id trên URL nếu đang có giá trị trong form search
  const currentStrategyId = tableRules.dataSearch.value["search_strategy_id"];
  if (currentStrategyId) {
    replaceQuery.search_strategy_id = currentStrategyId;
  }
  router
    .replace({
      name: "SearchFilter",
      query: replaceQuery,
    })
    .catch(() => {});
  try {
    const response = await mushroom.search_filter.listAsync(dataFilter);
    if (response.result) {
      dataTables.value = response.result;
      tableRules.total = response.meta.total;
      tableRules.page = response.meta.offset / response.meta.limit + 1;
    }
  } catch (e) {
    MethodService.showError(e.code);
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fn_hashUrl();
  getStrategyOptions();

  // Nếu có query param search_strategy_id → tự động lọc theo hướng tìm kiếm
  const queryStrategyId = route.query?.search_strategy_id;
  if (queryStrategyId) {
    tableRules.dataSearch.value["search_strategy_id"] = queryStrategyId;
    tableRules.filters = MethodService.filterTable(
      JSON.stringify(tableRules.dataSearch),
    );
  }

  getListSearchFilter();
});
</script>

<template>
  <Layout>
    <div class="card" style="min-height: 80vh">
      <div class="card-header">
        <h3 class="text-uppercase mb-0">
          {{ $t("t-list-search-filter") }}
          <template v-if="route.query?.search_strategy_id">
             {{ getStrategyName(route.query.search_strategy_id) }}
          </template>
        </h3>
        <div class="text-end">
          <div class="btn-header">
            <button
              class="btn btn-soft-secondary btn-border me-2"
              @click="fn_showFormTimKiem()"
            >
              <i class="ri-search-line"></i>
              {{ $t("t-search") }}
            </button>
            <button class="btn btn-secondary btn-border" @click="fn_ThemMoi">
              <i class="ri-add-circle-line"></i>
              {{ $t("t-add") }}
            </button>
          </div>
        </div>
      </div>
      <div class="card-body" style="padding: 0 0 15px 0">
        <div class="main-body">
          <div class="main-body-search mx-3 mb-3">
            <b-collapse
              v-model="tableRules.showFormSearch"
              id="myFormSearch"
              class="b-shadow"
            >
              <b-card>
                <el-form label-position="top" @submit.prevent>
                  <b-row>
                    <b-col md="4">
                      <el-form-item :label="$t('t-search-filter-code')">
                        <el-input
                          clearable
                          v-model="tableRules.dataSearch.value['code']"
                          :placeholder="$t('t-place-search-filter-code')"
                        ></el-input>
                      </el-form-item>
                    </b-col>
                    <b-col md="4">
                      <el-form-item :label="$t('t-search-filter-name-vi')">
                        <el-input
                          clearable
                          v-model="tableRules.dataSearch.value['name.vi']"
                          :placeholder="$t('t-place-search-filter-name-vi')"
                        ></el-input>
                      </el-form-item>
                    </b-col>
                    <b-col md="4">
                      <el-form-item :label="$t('t-search-filter-name-en')">
                        <el-input
                          clearable
                          v-model="tableRules.dataSearch.value['name.en']"
                          :placeholder="$t('t-place-search-filter-name-en')"
                        ></el-input>
                      </el-form-item>
                    </b-col>
                  </b-row>
                  <b-row class="align-items-end">
                    <b-col md="4">
                      <el-form-item :label="$t('t-search-filter-input-type')">
                        <el-select
                          v-model="tableRules.dataSearch.value['input_type']"
                          clearable
                          :placeholder="$t('t-place-search-filter-input-type')"
                          style="width: 100%"
                          :no-match-text="$t('t-match-data')"
                          :no-data-text="$t('t-no-data')"
                        >
                          <el-option
                            v-for="item in DataForm.inputTypeOptions"
                            :key="item.value"
                            :label="$t(item.labelKey)"
                            :value="item.value"
                          />
                        </el-select>
                      </el-form-item>
                    </b-col>
                    <b-col md="4">
                      <el-form-item :label="$t('t-search-filter-strategy')">
                        <el-select
                          v-model="
                            tableRules.dataSearch.value['search_strategy_id']
                          "
                          clearable
                          filterable
                          :placeholder="$t('t-place-search-filter-strategy')"
                          style="width: 100%"
                          :no-match-text="$t('t-match-data')"
                          :no-data-text="$t('t-no-data')"
                        >
                          <el-option
                            v-for="item in strategyOptions"
                            :key="item.value"
                            :label="item.label"
                            :value="item.value"
                          />
                        </el-select>
                      </el-form-item>
                    </b-col>
                    <b-col md="4">
                      <el-form-item>
                        <div class="d-flex gap-2">
                          <a
                            class="btn btn-soft-secondary btn-border"
                            style="width: 80px"
                            @click="fn_showFormTimKiem()"
                          >
                            {{ $t("t-hide") }}
                          </a>
                          <button
                            class="btn btn-secondary bg-gradient btn-border waves-effect waves-light"
                            style="width: 100px"
                            native-type="submit"
                            @click="fn_TimKiem()"
                          >
                            {{ $t("t-search") }}
                          </button>
                        </div>
                      </el-form-item>
                    </b-col>
                  </b-row>
                </el-form>
              </b-card>
            </b-collapse>
          </div>
          <div class="mb-3" style="float: right">
            <el-pagination
              small
              v-model:currentPage="tableRules.page"
              v-model:pageSize="pageSizeDefault"
              :page-sizes="tableRules.lengthMenu"
              background
              layout="total, sizes, prev, pager, next, jumper"
              :total="tableRules.total"
              @size-change="fn_tableSizeChange"
              @current-change="fn_tableCurentChange"
              @prev-click="fn_tablePrevClick"
              @next-click="fn_tableNextClick"
            />
          </div>
          <el-table
            size="small"
            :data="dataTables.value"
            style="width: 100%"
            max-height="550"
            v-loading="loading"
            :empty-text="$t('t-no-data')"
            @sort-change="fn_tableSortChange"
            :default-sort="{
              prop: tableRules.defaultSort[0],
              order: tableRules.defaultSort[1],
            }"
          >
            <el-table-column
              prop=""
              :label="$t('t-stt')"
              width="70"
              align="center"
            >
              <template #default="scope">
                <div class="text-center">
                  {{ tableRules.offset + scope.$index + 1 }}
                </div>
              </template>
            </el-table-column>
            <el-table-column
              prop="code"
              :label="$t('t-search-filter-code')"
              min-width="150"
              align="start"
              :sortable="true"
            />
            <el-table-column
              prop="name.vi"
              :label="$t('t-search-filter-name-vi')"
              min-width="180"
              align="start"
              :sortable="true"
            >
              <template #default="scope">
                {{ scope.row.name?.vi }}
              </template>
            </el-table-column>
            <el-table-column
              prop="name.en"
              :label="$t('t-search-filter-name-en')"
              min-width="180"
              align="start"
            >
              <template #default="scope">
                {{ scope.row.name?.en }}
              </template>
            </el-table-column>
            <el-table-column
              prop="input_type"
              :label="$t('t-search-filter-input-type')"
              min-width="120"
              align="center"
            >
              <template #default="scope">
                <span
                  v-if="scope.row.input_type === 'dropdown'"
                  class="badge badge-soft-info text-uppercase"
                >
                  {{ $t("t-input-type-dropdown") }}
                </span>
                <span
                  v-else-if="scope.row.input_type === 'textbox'"
                  class="badge badge-soft-warning text-uppercase"
                >
                  {{ $t("t-input-type-textbox") }}
                </span>
              </template>
            </el-table-column>
            <el-table-column
              prop="search_strategy_id"
              :label="$t('t-search-filter-strategy')"
              min-width="180"
              align="start"
            >
              <template #default="scope">
                {{ getStrategyName(scope.row.search_strategy_id) }}
              </template>
            </el-table-column>
            <el-table-column
              prop="tooltip"
              :label="$t('t-search-filter-tooltip')"
              min-width="180"
              show-overflow-tooltip
              align="start"
            >
              <template #default="scope">
                <div class="col-tooltip">{{ scope.row.tooltip }}</div>
              </template>
            </el-table-column>
            <el-table-column
              prop="index"
              :label="$t('t-search-filter-index')"
              width="100"
              align="center"
              :sortable="true"
            />
            <el-table-column
              prop="width"
              :label="$t('t-search-filter-width')"
              width="90"
              align="center"
            >
              <template #default="scope">
                {{ scope.row.width ? scope.row.width + '%' : '' }}
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('t-action')"
              align="center"
              width="150px"
            >
              <template #default="scope">
                <div class="btn-group-thao-tac">
                  <el-tooltip
                    :content="$t('t-edit')"
                    placement="top"
                    :enterable="false"
                    effect="light"
                  >
                    <button
                      type="button"
                      class="btn btn-soft-success waves-effect waves-light btn-sm"
                      @click="fn_handle('update', scope.$index, scope.row)"
                    >
                      <i class="ri-edit-box-line"></i>
                    </button>
                  </el-tooltip>
                  <el-tooltip
                    :content="$t('t-delete')"
                    placement="top"
                    :enterable="false"
                    effect="light"
                  >
                    <button
                      type="button"
                      class="btn btn-soft-danger waves-effect waves-light btn-sm"
                      @click="fn_handle('delete', scope.$index, scope.row)"
                    >
                      <i class="ri-delete-bin-6-line"></i>
                    </button>
                  </el-tooltip>
                </div>
              </template>
            </el-table-column>
          </el-table>
          <div class="mt-3" style="float: right">
            <el-pagination
              small
              v-model:currentPage="tableRules.page"
              v-model:pageSize="pageSizeDefault"
              :page-sizes="tableRules.lengthMenu"
              background
              layout="total, sizes, prev, pager, next, jumper"
              :total="tableRules.total"
              @size-change="fn_tableSizeChange"
              @current-change="fn_tableCurentChange"
              @prev-click="fn_tablePrevClick"
              @next-click="fn_tableNextClick"
            />
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
